import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';

import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { IBillingInfo } from 'checkout/types/Billing';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { PaymentType } from 'shared/enums/PaymentType';
import { CheckboxRadio } from 'shared/components/CheckboxRadio';

interface IBillingInfoProps {
  billingInfo: IBillingInfo;
  currentBillingInfoId?: number;
  defaultChecked?: boolean;
  onBillingInfoChange: (id: number) => void;
}

interface IBillingInfosProps {
  onBillingInfoChange?: (id: number) => void;
  currentBillingInfoId?: number;
}

const CreditCardBillingInfo = ({
  billingInfo,
  defaultChecked = false,
  onBillingInfoChange,
}: IBillingInfoProps) => {
  return (
    <CreditCardBillingInfo.Wrapper
      htmlFor={`${billingInfo.id}`}
      key={billingInfo.id}
      aria-label="billing info"
    >
      <CreditCardBillingInfo.Radio>
        <CheckboxRadio
          type="radio"
          id={`${billingInfo.id}`}
          key={`${billingInfo.id}`}
          name="billing info"
          value={billingInfo.id}
          aria-label="billing info"
          onChange={() => onBillingInfoChange(billingInfo.id)}
          checked={defaultChecked}
          data-qa="checkout_billing_info_radio"
        />
      </CreditCardBillingInfo.Radio>
      <CreditCardBillingInfo.Label>
        <CreditCardBillingInfo.LabelText>
          {billingInfo?.cardBrand} {billingInfo?.last4Digits}
        </CreditCardBillingInfo.LabelText>
      </CreditCardBillingInfo.Label>
    </CreditCardBillingInfo.Wrapper>
  );
};

export const CreditCardBillingInfos = ({
  onBillingInfoChange,
  currentBillingInfoId,
}: IBillingInfosProps) => {
  const { billingInfos: data, order } = useCheckoutContext();

  const billingInfos: IBillingInfo[] = Object.values(data)?.filter(
    (billingInfo) => billingInfo.paymentType === PaymentType.CreditCard,
  );

  const defaultBillingInfo = billingInfos.find(
    (billingInfo) => billingInfo?.id === order?.billingInfoId,
  );

  if (isEmpty(billingInfos)) {
    return null;
  }

  return (
    <CreditCardBillingInfos.FieldSet>
      {order?.billingInfoId && defaultBillingInfo && (
        <CreditCardBillingInfo
          billingInfo={defaultBillingInfo}
          defaultChecked={
            currentBillingInfoId === null ? true : currentBillingInfoId === order?.billingInfoId
          }
          onBillingInfoChange={onBillingInfoChange}
        />
      )}
      {billingInfos.map((billingInfo, index) =>
        billingInfo.id !== order?.billingInfoId ? (
          <CreditCardBillingInfo
            key={billingInfo.id}
            billingInfo={billingInfo}
            onBillingInfoChange={onBillingInfoChange}
            defaultChecked={
              currentBillingInfoId === null ? index === 0 : currentBillingInfoId === billingInfo.id
            }
          />
        ) : null,
      )}
    </CreditCardBillingInfos.FieldSet>
  );
};

CreditCardBillingInfos.FieldSet = styled.fieldset`
  border: none;
  padding: 0;
  max-height: 250px;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
  ${hideScrollbars};

  > label {
    font-size: 11.5px;
  }
`;

CreditCardBillingInfos.Text = styled.p`
  margin-bottom: 0;
`;

CreditCardBillingInfo.Wrapper = styled.label`
  color: ${({ theme }) => theme.colors.black};
  display: grid;
  grid-template-columns: [checkbox] 28px [billingInfo] auto [action];
  padding-bottom: 16px;
  cursor: pointer;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.small}px) {
    font-size: 11.5px;
  }
`;

CreditCardBillingInfo.Radio = styled.span`
  display: flex;
  grid-column-start: checkbox;
  grid-column-end: billingInfo;
`;

CreditCardBillingInfo.Label = styled.span`
  grid-column-start: billingInfo;
  grid-column-end: action;
`;

CreditCardBillingInfo.LabelText = styled.p`
  margin-block: 2.5px 0;
`;
